import type { CombatEvent, ServerCombatState, ServerCombatantState } from '../types';

// --- Public types ---

export interface CombatantSummary {
  id: string;
  name: string;
  side: 'player' | 'enemy';
  spriteKey: string;
  unitClass?: string;
  /** Damage dealt, broken down by element */
  damageByElement: Record<string, number>;
  totalDamageDealt: number;
  totalDamageTaken: number;
  totalHealingDone: number;
  critsLanded: number;
  weaknessHits: number;   // elementMultiplier > 1
  resistedHits: number;    // elementMultiplier < 1
  /** If this combatant died, what killed them */
  causeOfDeath?: {
    sourceName: string;
    abilityName: string;
    element?: string;
    damage: number;
  };
}

export interface CrawlSummary {
  totalTurns: number;
  encountersCompleted: number;
  combatants: Map<string, CombatantSummary>;
}

// Map DoT status effect IDs to elements for damage tracking
const DOT_ELEMENT_MAP: Record<string, string> = {
  burn: 'fire',
  bleed: 'steel',
  poison: 'nature',
  shock: 'lightning',
  frostbite: 'water',
  corrode: 'earth',
  wither: 'dark',
};

class CombatSummaryTrackerClass {
  private summary: CrawlSummary = this.freshSummary();
  /** Tracks which encounter IDs we've already counted to prevent duplicates */
  private countedEncounterIds = new Set<string>();

  /** Reset tracker for a new crawl */
  reset(): void {
    this.summary = this.freshSummary();
    this.countedEncounterIds.clear();
  }

  /** Feed a batch of combat events + the resulting state from one encounter */
  ingestEvents(events: CombatEvent[], state: ServerCombatState): void {
    // Register combatants from state (first time we see them)
    for (const c of state.combatants) {
      if (!this.summary.combatants.has(c.id)) {
        this.summary.combatants.set(c.id, this.freshCombatant(c));
      }
    }

    // Track last damage to each target for cause-of-death inference
    const lastDamage = new Map<string, { sourceName: string; abilityName: string; element?: string; damage: number }>();

    for (const event of events) {
      switch (event.type) {
        case 'damage': {
          if (!event.value || !event.sourceId || !event.targetId) break;
          const source = this.summary.combatants.get(event.sourceId);
          const target = this.summary.combatants.get(event.targetId);
          if (source) {
            const element = event.element ?? 'unknown';
            source.damageByElement[element] = (source.damageByElement[element] ?? 0) + event.value;
            source.totalDamageDealt += event.value;
            if (event.isCrit) source.critsLanded++;
            if (event.elementMultiplier != null && event.elementMultiplier > 1) source.weaknessHits++;
            if (event.elementMultiplier != null && event.elementMultiplier < 1) source.resistedHits++;
          }
          if (target) {
            target.totalDamageTaken += event.value;
          }
          // Track for cause of death
          const sourceName = source?.name ?? event.sourceId ?? 'Unknown';
          lastDamage.set(event.targetId, {
            sourceName,
            abilityName: event.abilityName ?? 'Attack',
            element: event.element,
            damage: event.value,
          });
          break;
        }

        case 'dot_tick': {
          if (!event.value || !event.targetId) break;
          const dotTarget = this.summary.combatants.get(event.targetId);
          if (dotTarget) {
            dotTarget.totalDamageTaken += event.value;
          }
          // DoT damage doesn't have a sourceId — attribute to the element
          const dotElement = DOT_ELEMENT_MAP[event.statusEffectId ?? ''] ?? 'unknown';
          lastDamage.set(event.targetId, {
            sourceName: event.statusEffectId ?? 'DoT',
            abilityName: event.statusEffectId ?? 'DoT',
            element: dotElement,
            damage: event.value,
          });
          break;
        }

        case 'heal': {
          if (!event.value || !event.sourceId) break;
          const healer = this.summary.combatants.get(event.sourceId);
          if (healer) {
            healer.totalHealingDone += event.value;
          }
          break;
        }

        case 'defeated': {
          if (!event.targetId) break;
          const dead = this.summary.combatants.get(event.targetId);
          if (dead) {
            const cause = lastDamage.get(event.targetId);
            if (cause) {
              dead.causeOfDeath = cause;
            }
          }
          break;
        }

        case 'victory':
          // Guard: only count each encounter once (backend can emit duplicate victory events)
          if (!this.countedEncounterIds.has(state.id)) {
            this.countedEncounterIds.add(state.id);
            this.summary.encountersCompleted++;
            this.summary.totalTurns += state.turn;
          }
          break;
      }
    }

    // Turns are captured per-encounter at victory time (see 'victory' case above)
  }

  /** Get the accumulated summary */
  getSummary(): CrawlSummary {
    return this.summary;
  }

  /** Get only player combatant summaries, sorted by damage dealt */
  getPlayerSummaries(): CombatantSummary[] {
    return Array.from(this.summary.combatants.values())
      .filter(c => c.side === 'player')
      .sort((a, b) => b.totalDamageDealt - a.totalDamageDealt);
  }

  /** Get cause-of-death entries for player combatants that died */
  getDeathReport(): CombatantSummary[] {
    return Array.from(this.summary.combatants.values())
      .filter(c => c.side === 'player' && c.causeOfDeath != null);
  }

  private freshSummary(): CrawlSummary {
    return {
      totalTurns: 0,
      encountersCompleted: 0,
      combatants: new Map(),
    };
  }

  private freshCombatant(c: ServerCombatantState): CombatantSummary {
    return {
      id: c.id,
      name: c.name,
      side: c.side,
      spriteKey: c.spriteKey,
      unitClass: c.unitClass,
      damageByElement: {},
      totalDamageDealt: 0,
      totalDamageTaken: 0,
      totalHealingDone: 0,
      critsLanded: 0,
      weaknessHits: 0,
      resistedHits: 0,
    };
  }
}

export const CombatSummaryTracker = new CombatSummaryTrackerClass();
